import React, { useState, useEffect } from 'react';
import { Typography, Button, Spin } from 'antd';
import TableWithSkeleton from '../../components/global/table/TableWithSkeleton';
import StatisticsCard from '../dashboard/components/StatisticsCard';
import Commission from '../../assets/Commission';
import TotalTrades from '../../assets/TotalTrades';
import Analytics from '../../assets/Analytics';
import Edit from '../../assets/Edit';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApproveIbRequestApi, GetPlanListApi, DeleteTempPlanApi, GetActiveIbPlanList } from '../../api/request/ibapproved';
import LoadableButton from '../../components/buttons/LoadableButton';
import toast from 'react-hot-toast';
import Delete from '../../assets/Delete';
import ConfirmationModal from '../../components/global/modal/ConfirmationModal';

const { Title, Text } = Typography;

const IbApproveList = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const { id, type } = useParams();
    const navigate = useNavigate();
    // Sample data from your JSON
    const [commissionData, setCommissionData] = useState({ multiConfigs: [], singleConfigs: [] });

    // Handle edit action
    const handleEdit = (record) => {
        console.log('Edit clicked for:', record);
        // Add your edit logic here
    };
    const handleDeleteClick = (record) => {
        setSelectedPlanForDelete(record);
        setIsDeleteConfirmModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedPlanForDelete) return;

        try {
            setIsDeleteLoading(true);
            const { data } = await DeleteTempPlanApi({ planId: selectedPlanForDelete.planId, userId: id }); // Adjust the ID field based on your data structure

            if (data?.success) {
                toast.success('Plan deleted successfully!');
                setIsDeleteConfirmModal(false);
                setSelectedPlanForDelete(null);
                // Refresh the data
                getCommissionData();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to delete plan');
        } finally {
            setIsDeleteLoading(false);
        }
    };

    // Add these new state variables for delete confirmation modal
    const [isDeleteConfirmModal, setIsDeleteConfirmModal] = useState(false);
    const [selectedPlanForDelete, setSelectedPlanForDelete] = useState(null);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    // Static Plans Table Columns
    const staticPlansColumns = [
        {
            title: 'Plan Name',
            dataIndex: 'planName',
            key: 'planName',
            render: (text) => <span className="font-medium text-gray-800">{text}</span>
        },
        {
            title: 'Group',
            dataIndex: 'groupName',
            key: 'groupName',
            render: (text) => (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium">
                    {text}
                </span>
            )
        },
        {
            title: 'Rate',
            dataIndex: 'commission',
            key: 'commission',
            render: (commission) => (
                <span className="font-semibold">
                    $ {commission}
                </span>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 50,
            align: 'center',
            render: (text, record) => (
                <div className='flex'>
                    <Link to={`/ib_config/${type}/${id}/${record.planId}`} className='cursor-pointer'>
                        <Edit color="#6B7280" size={16} />
                    </Link>
                    {
                        type === "pending" && (
                            <div onClick={() => handleDeleteClick(record)} className='cursor-pointer'>
                                <Delete color="#6B7280" size={16} />
                            </div>
                        )
                    }
                </div>
            )
        }
    ];

    const getCommissionData = async () => {
        try {
            setIsLoading(true);
            if (type === "pending") {
                const { data } = await GetPlanListApi(id);
                setCommissionData(data?.data)
            } else {
                const { data } = await GetActiveIbPlanList(id);
                setCommissionData(data?.data)
            }
        } catch (err) { }
        finally { setIsLoading(false) }
    }

    useEffect(() => {
        getCommissionData();
    }, [id])

    // Calculate maximum levels for dynamic columns
    const getMaxLevels = () => {
        if (!commissionData.multiConfigs || commissionData.multiConfigs.length === 0) return 0;
        return Math.max(...commissionData.multiConfigs.map(plan => plan.levels?.length || 0));
    };

    // Generate dynamic columns for multilevel table
    const generateMultilevelColumns = () => {
        const maxLevels = getMaxLevels();
        const columns = [
            {
                title: 'Plan Name',
                dataIndex: 'planName',
                key: 'planName',
                render: (text) => <span className="font-medium text-gray-800">{text}</span>,
                width: 120
            }
        ];

        // Add level columns dynamically
        for (let i = 1; i <= maxLevels; i++) {
            columns.push({
                title: `Level ${i}`,
                dataIndex: `level${i}`,
                key: `level${i}`,
                width: 100,
                align: 'center',
                render: (text, record) => {
                    const levelData = record.levels?.find(level => level.level === i);
                    if (levelData && levelData.rate > 0) {
                        return (
                            <span className="font-semibold">
                                $ {levelData.rate}
                            </span>
                        );
                    }
                    return <span className="text-gray-400">-</span>;
                }
            });
        }

        // Add Actions column
        columns.push({
            title: 'Actions',
            key: 'actions',
            align: 'center',
            width: 80,
            render: (text, record) => {
                console.log(record)
                return (
                    <div className='flex justify-center'>
                        <button onClick={() => navigate(`/ib_config/${type}/${id}/${record.planId}`)} className={`${record?.parentCommissionType == 'multilevel' && 'opacity-30'}`} disabled={record?.parentCommissionType == 'multilevel'}>
                            <Edit color="#6B7280" size={16} />
                        </button>
                        {
                            type === "pending" && (
                                <button onClick={() => handleDeleteClick(record)} className='cursor-pointer'>
                                    <Delete color="#6B7280" size={16} />
                                </button>
                            )
                        }
                    </div>
                )
            }
        });

        return columns;
    };

    // Prepare data for multilevel table
    const prepareMultilevelData = () => {
        return commissionData.multiConfigs.map((plan, index) => ({
            ...plan,
            key: plan.planId
        }));
    };

    // Prepare data for static table
    const prepareStaticData = () => {
        return commissionData.singleConfigs.map(plan => ({
            key: plan.planId,
            ...plan
        }));
    };

    // Statistics cards data following Overview.jsx pattern
    const statisticsCards = [
        {
            title: 'Static Plans',
            value: commissionData.singleConfigs.length,
            icon: <Commission color="#7367F0" />,
            bgColor: "bg-[#E9E7FD]"
        },
        {
            title: 'Multilevel Plans',
            value: commissionData.multiConfigs.length,
            icon: <TotalTrades color="#28C76F" />,
            bgColor: "bg-[#DDF6E8]"
        },
        {
            title: 'Maximum Levels',
            value: getMaxLevels(),
            icon: <Analytics color="#00BAD1" />,
            bgColor: "bg-[#D6F4F8]"
        }
    ];

    const approveIbRequest = async () => {
        try {
            setIsApproving(true);
            const params = { userId: id, isNewRecord: true, isApproved: true, comment: "" }
            const { data } = await ApproveIbRequestApi(params);
            if (data?.success) {
                toast.success('IB Request Approved');
                navigate('/ib_manager?filter=Approved')
            } else {
                toast.error(data?.message || 'Error approving IB request');
            }
        } catch (err) { }
        finally { setIsApproving(false) }
    }
    return (
        <>
            <div className="p-6 min-h-screen">
                {isLoading ? <div className="flex justify-center flex-col my-[10%]">
                    <Spin size="large" />
                    <p className="primary text-center mt-2">Loading...</p>
                </div> :
                    <div className="w-full mx-auto">
                        {/* Commission Data Tables */}
                        <div className="space-y-8">
                            {/* Static Plans Table */}
                            <div className="">
                                <div className='flex justify-between mb-6 items-center'>
                                    <div className="">
                                        <Title level={4} className=" text-gray-800">
                                            Static Commission Plans
                                        </Title>
                                        <Text className="text-gray-600">
                                            Fixed commission rates for static plans
                                        </Text>
                                    </div>
                                    <div className='flex'>
                                        {type === "pending" && <LoadableButton className='bg-green-600 text-white px-4 py-2 rounded-md me-2'
                                            onClick={e => approveIbRequest()}
                                            isLoading={isApproving}
                                            disabled={isApproving}
                                            loadingLable="Approving..."
                                            lable="Approve IB Request" />}
                                        <Link to={`/ib_config/${type}/${id}/0`} className='bg-primary text-white px-4 py-2 rounded-md'>Add New</Link>
                                    </div>
                                </div>
                                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                    <TableWithSkeleton
                                        columns={staticPlansColumns}
                                        data={prepareStaticData()}
                                        loading={false}
                                        showIndexColumn={true}
                                        pagination={false}
                                        rowKey="planId"
                                    />
                                </div>
                            </div>

                            {/* Multilevel Plans Table */}
                            <div className="shadow-sm">
                                <div className="mb-6">
                                    <Title level={4} className=" text-gray-800">
                                        Multilevel Commission Plans
                                    </Title>
                                    <Text className="text-gray-600">
                                        Dynamic commission rates across multiple levels (up to {getMaxLevels()} levels)
                                    </Text>
                                </div>
                                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                    <TableWithSkeleton
                                        columns={generateMultilevelColumns()}
                                        data={prepareMultilevelData()}
                                        loading={isLoading}
                                        showIndexColumn={true}
                                        pagination={false}
                                        rowKey="planId"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Summary Card using StatisticsCard component */}
                        <div className="mt-8">
                            <div className="border-2 border-light rounded-[10px] p-5">
                                <div className="flex flex-col gap-5 h-full">
                                    <h2 className="text-title font-semibold">Commission Plans Summary</h2>

                                    <div className="grid md:grid-cols-3 gap-5">
                                        {statisticsCards?.map((card, index) => (
                                            <StatisticsCard key={index} {...card} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
            <ConfirmationModal
                isOpen={isDeleteConfirmModal}
                setIsOpen={setIsDeleteConfirmModal}
                type='error'
                message={`Are you sure you want to delete "${selectedPlanForDelete?.planName || 'this plan'}" ? `}
                onConfirm={handleDeleteConfirm}
                isLoading={isDeleteLoading}
                loadingLabel='Deleting plan...'
            />
        </>
    );
}

export default IbApproveList;